// The viewer: a row's attachments, over the tracker.
//
// The list on one side, the selected file on the other: an image inline, a PDF in the browser's
// own viewer through an inline URL, anything else as a download. Each file downloads on its own;
// Download all is one zip the API streams, fetched with the caller's token and handed to the
// browser as a file. The owner can delete a file here, confirmed in place - a second click on the
// same control - so the question and the answer sit where the file is.
//
// At phone width the dialog is a full-screen sheet: the list first, the preview beneath it, the
// controls the width of the screen. Nothing in it scrolls sideways.

import { useEffect, useState } from 'react';

import {
  downloadAttachmentsZip,
  deleteAttachment,
  getAttachmentDownload,
  listAttachments,
} from '../../api.js';
import Icon from '../../ui/Icon.jsx';
import Modal from '../../ui/Modal.jsx';
import { formatBytes } from './uploads.js';

export default function AttachmentsDialog({ leg, row, onClose, onChanged }) {
  const [attachments, setAttachments] = useState(null);
  const [selected, setSelected] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(null);

  useEffect(() => {
    let cancelled = false;
    listAttachments(leg, row.delivery_id)
      .then((listed) => {
        if (cancelled) return;
        setAttachments(listed);
        setSelected(listed[0] ?? null);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [leg, row.delivery_id]);

  // The preview URL is short-lived and per file; fetched when the selection changes, dropped
  // when it changes again so a slow answer for the last file cannot land on this one.
  useEffect(() => {
    if (!selected || !previewable(selected)) {
      setPreview(null);
      return undefined;
    }
    let cancelled = false;
    setPreview(null);
    getAttachmentDownload(leg, row.delivery_id, selected.attachment_id, { inline: true })
      .then(({ download_url: url }) => {
        if (!cancelled) setPreview(url);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [leg, row.delivery_id, selected]);

  const download = async (attachment) => {
    setError(null);
    try {
      const { download_url: url } = await getAttachmentDownload(
        leg, row.delivery_id, attachment.attachment_id,
      );
      open(url, attachment.filename);
    } catch (err) {
      setError(err.message);
    }
  };

  const remove = async (attachment) => {
    if (confirming !== attachment.attachment_id) {
      setConfirming(attachment.attachment_id);
      return;
    }
    setError(null);
    try {
      await deleteAttachment(leg, row.delivery_id, attachment.attachment_id);
      const remaining = attachments.filter((a) => a.attachment_id !== attachment.attachment_id);
      setAttachments(remaining);
      setConfirming(null);
      if (selected?.attachment_id === attachment.attachment_id) setSelected(remaining[0] ?? null);
      onChanged(remaining.length);
      if (remaining.length === 0) onClose();
    } catch (err) {
      setError(err.message);
      setConfirming(null);
    }
  };

  return (
    <Modal onClose={onClose}>
      <div className="attachments-dialog">
        <header className="attachments-dialog-head">
          <h3>Attachments · <code>{row.delivery_id}</code></h3>
          <div className="attachments-dialog-controls">
            {attachments && attachments.length > 0 && (
              <button type="button" className="button"
                      onClick={() => downloadAttachmentsZip(leg, row.delivery_id).catch((err) => setError(err.message))}>
                <Icon name="download" /> Download all
              </button>
            )}
            <button type="button" className="icon-button quiet" onClick={onClose}
                    aria-label="Close" title="Close">
              <Icon name="close" />
            </button>
          </div>
        </header>

        {error && <p className="error">{error}</p>}
        {!attachments && !error && <p className="muted">Loading…</p>}

        {attachments && (
          <div className="attachments-dialog-body">
            <ul className="attachment-list" aria-label="Files">
              {attachments.map((attachment) => (
                <li key={attachment.attachment_id}
                    className={selected?.attachment_id === attachment.attachment_id ? 'selected' : ''}>
                  <button type="button" className="attachment-select"
                          onClick={() => setSelected(attachment)}
                          aria-pressed={selected?.attachment_id === attachment.attachment_id}>
                    <span className="attachment-name">
                      <strong>{attachment.filename}</strong>
                      <small>
                        {formatBytes(attachment.size_bytes ?? 0)} · {attachment.uploaded_by}
                      </small>
                    </span>
                  </button>
                  <span className="row-actions">
                    <button type="button" className="icon-button quiet"
                            onClick={() => download(attachment)}
                            aria-label={`Download ${attachment.filename}`} title="Download">
                      <Icon name="download" />
                    </button>
                    {row.mine && (
                      <button type="button"
                              className={`icon-button quiet${confirming === attachment.attachment_id ? ' confirming' : ''}`}
                              onClick={() => remove(attachment)}
                              onBlur={() => setConfirming(null)}
                              aria-label={confirming === attachment.attachment_id
                                ? `Confirm delete ${attachment.filename}`
                                : `Delete ${attachment.filename}`}
                              title={confirming === attachment.attachment_id ? 'Click again to delete' : 'Delete'}>
                        <Icon name="trash" />
                      </button>
                    )}
                  </span>
                </li>
              ))}
            </ul>

            <div className="attachment-preview">
              {selected && previewable(selected) && !preview && <p className="muted">Loading…</p>}
              {selected && preview && selected.content_type.startsWith('image/') && (
                <img src={preview} alt={selected.filename} />
              )}
              {selected && preview && selected.content_type === 'application/pdf' && (
                <iframe src={preview} title={selected.filename} />
              )}
              {selected && !previewable(selected) && (
                <p className="muted">No preview for this file type; download it to open it.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

const previewable = (attachment) =>
  attachment.content_type.startsWith('image/') || attachment.content_type === 'application/pdf';

// A signed URL is cross-origin, so `download` on a link is ignored and the browser navigates;
// opening it in a new tab keeps the tracker where it is.
function open(url) {
  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  link.rel = 'noopener';
  link.click();
}
